import {
  Children,
  cloneElement,
  createRef,
  forwardRef,
  isValidElement,
  PropsWithChildren,
  ReactElement,
  Ref, useImperativeHandle,
  useMemo,
  useState
} from "react";
import { IconChevronDown } from "@tabler/icons-react";
import { classNames } from "@ct-react/core";
import "./collapse.scss";

type ValidChildren = ReactElement<typeof Item> | ReactElement<typeof Item>[];

type Props = {
  autoClose?: boolean;
  animated?: boolean;
  children: ValidChildren;
}

type ItemHandler = {
  open: () => void;
  close: () => void;
}

type ItemProps = PropsWithChildren & {
  className?: string;
  title: string;
  keepOpen?: boolean;
  open?: boolean;
  onOpen?: () => void;
}

const Collapse = ({ autoClose = false, animated = true, children }: Props) => {

  const childArray = Children.toArray(children) as ReactElement<typeof Item>[];
  const refs = useMemo(() => childArray.map(() => createRef<ItemHandler>()), [ childArray.length ]);

  const handleOpen = (index: number) => refs.forEach((ref, i) => {
    (index !== i) && ref.current?.close();
  });

  if (!autoClose)
    return children;

  return Children.toArray(children).map((child, i) => isValidElement(child)
    ? cloneElement(child, {
      ref: refs[i],
      className: classNames((child.props as ItemProps).className, { animated }),
      onOpen: () => handleOpen(i),
    } as Partial<ItemProps> & { ref: Ref<ItemHandler> })
    : child);

}

export const Item = forwardRef<ItemHandler, ItemProps>((
  {
    className,
    title,
    keepOpen,
    open: initOpen = false,
    onOpen = () => void 0,
    children
  }, forwardedRef) => {

  const [ open, setOpen ] = useState<boolean>(keepOpen || initOpen);

  useImperativeHandle(forwardedRef, () => ({
    open: () => !keepOpen && setOpen(true),
    close: () =>!keepOpen && setOpen(false)
  }));

  const onToggle = () => {
    if (!!keepOpen) return;
    setOpen(!open);
    !open && onOpen();
  }

  const wrapperClasses = classNames("collapse-item", className, { show: open });

  return (
    <div className={wrapperClasses}>
      <button type="button"
              className="ci-header"
              onClick={onToggle}>
        {title}
        {!keepOpen &&
          <IconChevronDown className="ci-icon" />
        }
      </button>
      <div className="ci-body">
        <div className="ci-bwrap">
          {children}
        </div>
      </div>
    </div>);

});

export default Collapse;
